<template>
  <div>
    <form
      class="text-left"
      @submit.prevent="submit"
    >
      <label class="mb-0 small font-weight-bold">
        {{ $t('plants.name') }}
      </label>
      <BFormInput
        v-model="name"
        :placeholder="`${$t('plants.name')}...`"
        class="mb-2"
      />
      <label class="mb-0 small font-weight-bold">
        {{ $t('plants.code') }}
      </label>
      <BFormInput
        v-model="code"
        :placeholder="`${$t('plants.code')}...`"
        class="mb-2"
      />
      <label class="mb-0 small font-weight-bold">
        {{ $t('elements.location') }}
      </label>
      <ElementSelector
        :element-id.sync="parentId"
        :elements="filteredPlantElements"
        :plant="plant"
        class="mb-2"
      />
      <label class="mb-0 small font-weight-bold">
        {{ $t('general.plant') }}
      </label>
      <BFormInput
        :value="plantName"
        disabled
        class="mb-2"
      />

      <div
        v-if="element && (loadingPlantTaxonomies || loadingElementAssignedTaxonomies)"
        class="text-center mt-3"
      >
        <BSpinner
          small
          variant="primary"
        />
      </div>
      <div v-else-if="element && plantTaxonomies.length > 0">
        <p class="mb-0 mt-4 text-muted small font-weight-bold">
          {{ $t('taxonomies.assignTo').toUpperCase() }}
        </p>
        <hr class="mt-1">

        <div
          v-for="plantTaxonomy in plantTaxonomies"
          :key="plantTaxonomy.id"
        >
          <label class="mb-0 small font-weight-bold">
            {{ plantTaxonomy.description || plantTaxonomy.code || '-' }}
          </label>
          <TaxonomyElementSelect
            v-model="plantTaxonomiesAssigns[plantTaxonomy.id]"
            :taxonomy-nodes="plantTaxonomy.nodes"
            :plant="plant"
          />
        </div>
      </div>

      <div v-if="error">
        <hr
          class="w-25"
          style="margin-top: 10px; margin-bottom: 10px"
        >
        <div class="text-center text-danger error ">
          {{ error.title }}
          <div
            v-for="(err, index) in (error.list || [])"
            :key="index"
            class="small"
          >
            {{ err }}
          </div>
        </div>
      </div>

      <div class="mt-3">
        <SaveButton
          :pending="pending"
          :disabled="disabled"
          :show-remove="!!element"
          :remove-pending="removePending"
          @remove="remove"
          @cancel="$emit('close')"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { handleApiError } from '@/utils/handleApiError';
import { elementType } from '@/utils/dictionary';
import TaxonomyElementSelect from '@/components/taxonomies/TaxonomyElementSelect';
import ElementSelector from './ElementSelector';

export default {
  props: {
    plant: {
      type: Object,
      required: true,
    },
    element: Object,
    plantElements: Array,
  },
  data: () => ({
    id: null,
    parentId: null,
    plantId: null,
    name: '',
    code: '',
    type: null,
    pending: false,
    removePending: false,
    error: null,
    plantName: null,
    loadingPlantTaxonomies: false,
    loadingElementAssignedTaxonomies: false,
    plantTaxonomies: [],
    plantTaxonomiesAssigns: {},
    originalPlantTaxonomiesAssigns: {},
    elementTypeEnum: elementType,
  }),
  components: {
    TaxonomyElementSelect,
    ElementSelector,
  },
  computed: {
    ...mapGetters([
      'elementType',
    ]),
    disabled() {
      return !this.name || !this.code || !this.parentId;
    },
    filteredPlantElements() {
      if (!this.plantElements) return [];
      return this.plantElements.filter(x => x.id !== this.id);
    },
  },
  watch: {
    plantTaxonomies() {
      this.plantTaxonomiesAssigns = this.plantTaxonomies.reduce((acc, curr) => {
        acc[curr.id] = '';
        return acc;
      }, {});
      this.originalPlantTaxonomiesAssigns = { ...this.plantTaxonomiesAssigns };
    },
    parentId(v) {
      this.type = this.parseStartingTypeBasedOnParentId(v);
    },
  },
  methods: {
    ...mapActions([
      'createElement',
      'updateElement',
      'deleteElement',
      'getTaxonomiesAssignmentData',
      'getTaxonomiesForElement',
      'assignTaxonomyForElement',
      'unassignTaxonomyForElement',
      'getUserElements',
    ]),
    submit() {
      if (this.element) {
        this.update();
      } else {
        this.create();
      }
    },
    create() {
      this.pending = true;
      this.createElement({
        data: {
          plantId: this.plantId,
          parentId: this.parentId,
          name: this.name,
          code: this.code,
          type: this.type,
        },
      })
        .catch(({ response }) => {
          handleApiError(response);
        })
        .finally(() => {
          this.pending = false;
          this.$emit('close');
        });
    },
    async update() {
      this.pending = true;
      try {
        await this.updateTaxonomiesForElement();
        await this.updateElement({
          params: {
            id: this.element.id,
            plantId: this.plantId,
          },
          data: {
            parentId: this.parentId,
            plantId: this.plantId,
            name: this.name,
            code: this.code,
            type: this.type,
          },
        });
      } catch ({ response }) {
        handleApiError(response);
      } finally {
        this.pending = false;
        this.$emit('close');
      }
    },
    async fetchTaxonomiesForElement() {
      try {
        this.loadingElementAssignedTaxonomies = true;
        const { data } = await this.getTaxonomiesForElement({
          params: {
            elementId: this.element.id,
            plantId: this.plantId,
          },
        });
        data
          .filter(t => t.namespaceId === this.plant.id)
          .forEach(t => {
            this.plantTaxonomiesAssigns[t.taxonomyId] = t.taxonomyNodeId;
            this.originalPlantTaxonomiesAssigns[t.taxonomyId] = t.taxonomyNodeId;
          });
        this.loadingElementAssignedTaxonomies = false;
      } catch ({ response }) {
        handleApiError(response);
      }
    },
    async updateTaxonomiesForElement() {
      try {
        await Promise.all(Object.keys(this.plantTaxonomiesAssigns).map(async k => {
          if (this.originalPlantTaxonomiesAssigns[k] === this.plantTaxonomiesAssigns[k]) return;

          if (this.plantTaxonomiesAssigns[k]) {
            if (this.originalPlantTaxonomiesAssigns[k]) {
              await this.unassignTaxonomyForElement({
                params: {
                  elementId: this.element.id,
                  plantId: this.plantId,
                  query: {
                    taxonomyNodeId: this.originalPlantTaxonomiesAssigns[k],
                  },
                },
              });
            }

            await this.assignTaxonomyForElement({
              params: {
                elementId: this.element.id,
                plantId: this.plantId,
              },
              data: {
                namespaceId: this.plantId,
                taxonomyId: k,
                taxonomyNodeId: this.plantTaxonomiesAssigns[k],
              },
            });
          } else {
            await this.unassignTaxonomyForElement({
              params: {
                elementId: this.element.id,
                plantId: this.plantId,
                query: {
                  taxonomyNodeId: this.originalPlantTaxonomiesAssigns[k],
                },
              },
            });
          }
        }));
      } catch ({ response }) {
        handleApiError(response);
      } finally {
        this.$emit('taxonomy-update');
      }
    },
    async remove() {
      this.removePending = true;

      try {
        await this.deleteElement({
          params: {
            id: this.id,
            plantId: this.plantId,
          },
        });
        await this.getUserElements();
      } catch ({ response }) {
        handleApiError(response);
      } finally {
        this.removePending = false;
        this.$emit('close');
      }
    },
    async fetchElementPlantTaxonomies() {
      try {
        this.loadingPlantTaxonomies = true;
        const { data } = await this.getTaxonomiesAssignmentData({
          params: {
            query: {
              namespaceId: this.plantId,
            },
          },
        });
        this.plantTaxonomies = data;
        this.loadingPlantTaxonomies = false;
      } catch ({ response }) {
        handleApiError(response);
      }
    },
    parseStartingTypeBasedOnParentId(parentId) {
      const type = this.elementType(parentId);
      switch (type) {
        case this.elementTypeEnum.area:
          return this.elementTypeEnum.line;
        case this.elementTypeEnum.line:
          return this.elementTypeEnum.machine;
        default:
          return this.elementTypeEnum.area;
      }
    },
  },
  async created() {
    if (this.element) {
      this.id = this.element.id;
      this.parentId = this.element.parentId;
      this.name = this.element.name;
      this.code = this.element.code;
      this.type = this.element.type;
    } else {
      this.parentId = null;
    }

    this.plantName = this.plant.name;
    this.plantId = this.plant.id;

    if (this.element) {
      await this.fetchElementPlantTaxonomies();
      await this.fetchTaxonomiesForElement();
    }
  },
};
</script>
